import React, { useCallback, useEffect, useState } from 'react';
import { Joyride, EVENTS, STATUS } from 'react-joyride';
import type { EventData, Controls, Props as IJoyProps, Step as IStepProps } from 'react-joyride';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { theme } from 'antd';

import eduzzAccountsService from 'v2/services/eduzzAccount';
import { uiManagerService } from 'v2/services/uiManager';

interface IProps extends IJoyProps {
  name: string;
}

const defaultStepProps: Partial<IStepProps> = {
  skipBeacon: true,
  dismissKeyAction: false,
  overlayClickAction: false,
  showProgress: true
};

const Tour = ({ name, steps, ...props }: IProps) => {
  const { token } = theme.useToken();
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [currentlyTour] = useObservable(() => uiManagerService.getCurrentlyTour(), []);
  const [isModalOpen] = useObservable(() => uiManagerService.getIsModalOpen(), []);
  const [tours] = useObservable(() => eduzzAccountsService.getTours(), []);
  const [saveTour] = useCallbackObservable(() => eduzzAccountsService.saveTourAction(name), []);
  const [loadingTours] = useObservable(() => eduzzAccountsService.getLoadingTours(), []);

  const [tourName] = useState(() => `tour-${name}`);

  const onEvent = useCallback(
    (data: EventData, _controls: Controls) => {
      const userDidntSeeTour = !tours?.includes(name);

      if (data.type === EVENTS.TOUR_END && data.status === STATUS.FINISHED && userDidntSeeTour) {
        saveTour();
        uiManagerService.setCurrentlyTour(null);
      }
    },
    [name, saveTour, tours]
  );

  useEffect(() => {
    const userDidntSeeTour = tours !== null && !tours?.includes(name);
    const userIsNotSupport = user !== null && !user.is_support;
    const shouldShow = userDidntSeeTour && userIsNotSupport && !loadingTours;

    if (shouldShow) {
      uiManagerService.setCurrentlyTour(tourName);
    }
  }, [currentlyTour, loadingTours, name, tourName, tours, user]);

  const shouldShowTour = currentlyTour === tourName && !isModalOpen;
  const nSteps: IStepProps[] = steps.map((s: IStepProps) => ({ ...s, ...defaultStepProps }));

  return (
    <Joyride
      locale={{
        back: 'Voltar',
        close: 'Fechar',
        last: 'Entendi',
        next: 'Próximo',
        nextWithProgress: 'Próximo ({current} de {total})',
        skip: 'Pular'
      }}
      scrollToFirstStep={true}
      run={shouldShowTour}
      steps={nSteps}
      continuous={true}
      onEvent={onEvent}
      options={{
        primaryColor: token.colorPrimary,
        textColor: token.colorText,
        zIndex: 1301,
        buttons: ['back', 'primary']
      }}
      {...props}
    />
  );
};

export default Tour;
